import { subject } from "@casl/ability";
import { Text, Anchor, Badge, Button, Group } from "@mantine/core";
import { modals } from "@mantine/modals";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useCallback } from "react";
import { NavLink } from "react-router-dom";
import { formatDate } from "@/utils/date";
import ContextMenuRow from "./ContextMenuRow";
import AvaliacaoIAModalContent from "../ideia-avaliacao-ia/AvaliacaoIAModalContent";
import IdeiaStatusBadge from "../ideia-status-badge";
import { IdeiaUserPatrocinio } from "../ideia-user-patrocinio";

/**
 *
 * @param ideia
 */
function openAvaliacaoIAModal(ideia: TIdeia): void {
  modals.open({
    title: "Resultado da Avaliação",
    size: "lg",
    children: <AvaliacaoIAModalContent ideia={ideia} />
  });
}

type AvaliacaoIAButtonProperties = {
  ideia: TIdeia;
  status: TIdeia["avaliacaoIAStatus"];
  percentual: number | null | undefined;
};

/**
 * @param root0
 * @param root0.ideia
 * @param root0.status
 * @param root0.percentual
 */
function AvaliacaoIAButton({ ideia, status, percentual }: AvaliacaoIAButtonProperties) {
  const handleOpenAvaliacao = useCallback(() => {
    openAvaliacaoIAModal(ideia);
  }, [ideia]);

  if (status === "FAILED") {
    return (
      <Badge color="red" size="sm">
        Falhou
      </Badge>
    );
  }

  if (status !== "COMPLETED" || typeof percentual !== "number") {
    return (
      <Badge color="gray" size="sm">
        N/A
      </Badge>
    );
  }

  return (
    <Button size="xs" variant="light" onClick={handleOpenAvaliacao}>
      {percentual}%
    </Button>
  );
}

const columnHelper = createColumnHelper<TIdeia>();
const IA_COLUMN_ID = "avaliacaoIAScorePercentual";

/**
 *
 * @param columnsToFilter
 * @param canViewIA
 */
export function filterIdeiasColumnsByIAAccess(
  columnsToFilter: ColumnDef<TIdeia>[],
  canViewIA: boolean
): ColumnDef<TIdeia>[] {
  if (canViewIA) {
    return columnsToFilter;
  }

  return columnsToFilter.filter((column) => {
    const columnId = "id" in column ? column.id : undefined;
    const accessorKey = "accessorKey" in column ? column.accessorKey : undefined;
    return columnId !== IA_COLUMN_ID && accessorKey !== IA_COLUMN_ID;
  });
}

const tituloColumn = columnHelper.accessor("titulo", {
  header: "Titulo",
  enableSorting: true,
  enableColumnFilter: true,
  cell({ row, getValue }) {
    return (
      <Group gap="xs">
        <Anchor component={NavLink} to={`/ideias/${row.original.id}`}>
          {getValue()}
        </Anchor>
        <IdeiaStatusBadge status={row.original.status} />
      </Group>
    );
  }
});

const setoresColumn = columnHelper.accessor("setores", {
  header: "Setor",
  enableSorting: true,
  enableColumnFilter: true,
  cell({ getValue }) {
    const setores = getValue();
    return (
      <Group gap="xs">
        {setores.map((setor) => (
          <Badge key={setor} variant="default" color="gray">
            {setor}
          </Badge>
        ))}
      </Group>
    );
  }
});

const publishedAtColumn = columnHelper.accessor("publishedAt", {
  header: "Publicado",
  enableSorting: true,
  enableColumnFilter: true,
  cell({ getValue }) {
    const publishedAt = getValue();
    if (publishedAt) {
      return <Text>{formatDate(publishedAt, { year: "numeric", month: "short", day: "numeric" })}</Text>;
    }
    return (
      <Badge color="gray" size="sm">
        Em Rascunho
      </Badge>
    );
  }
});

const avaliacaoIAColumn = columnHelper.accessor(IA_COLUMN_ID, {
  header: "Aderência",
  enableSorting: true,
  enableColumnFilter: false,
  cell({ row, getValue }) {
    const status = row.original.avaliacaoIAStatus;
    const percentual = getValue();
    return <AvaliacaoIAButton ideia={row.original} status={status} percentual={percentual} />;
  }
});

const actionsColumn = columnHelper.display({
  id: "actions",
  cell: (properties) => {
    const ideia = subject("TIdeia", properties.row.original);
    return (
      <Group justify="flex-end" gap={2}>
        <ContextMenuRow ideia={ideia} />
      </Group>
    );
  }
});

const columns: ColumnDef<TIdeia>[] = [tituloColumn, setoresColumn, publishedAtColumn, avaliacaoIAColumn, actionsColumn];

export default columns;

export const columnsIdeiasMobile: ColumnDef<TIdeia>[] = [
  tituloColumn,
  publishedAtColumn,
  avaliacaoIAColumn,
  actionsColumn
];

export const patrocinioColumn = columnHelper.display({
  id: "patrocinio",
  header: "Patrocinio",
  cell: (properties) => {
    const ideia = subject("TIdeia", properties.row.original);
    return (
      <Group justify="start" gap={2}>
        <IdeiaUserPatrocinio ideia={ideia} />
      </Group>
    );
  }
});
